import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UserApi } from "@/api/user.api";
import { APIClient } from "@/api/axiosClient";

const VerifyMfa: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [code, setCode] = React.useState(["", "", "", "", "", ""]);
  const [isVerifying, setIsVerifying] = React.useState(false);
  const username = location.state?.email || "";

  const handleChange = (idx: number, value: string) => {
    const val = value.replace(/[^0-9]/g, "");
    if (!val) return;
    const newCode = [...code];
    newCode[idx] = val;
    setCode(newCode);
    // Move to next box
    if (val && idx < 5) {
      const next = document.getElementById(`mfa-input-${idx + 1}`);
      if (next) (next as HTMLInputElement).focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number
  ) => {
    if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();
      const newCode = [...code];
      if (code[idx]) {
        newCode[idx] = "";
        setCode(newCode);
      } else if (idx > 0) {
        const prev = document.getElementById(`mfa-input-${idx - 1}`);
        if (prev) (prev as HTMLInputElement).focus();
        newCode[idx - 1] = "";
        setCode(newCode);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const mfaCode = code.join("");
    if (mfaCode.length !== 6) {
      toast.error("Please enter the 6-digit code.");
      return;
    }
    setIsVerifying(true);
    try {
      await UserApi.verifyMfa({ username, mfaCode });
      // Fetch and store user info after successful MFA
      const userInfoResponse = await APIClient("/user/getCurrentUserInfo");
      sessionStorage.setItem("userInfo", JSON.stringify(userInfoResponse.data));
      localStorage.setItem("isAuthenticated", "true");
      toast.success("MFA verified! Logging you in...");
      // Redirect to dashboard or home
      navigate("/accesspoint");
    } catch (err: any) {
      let errorMsg = "Invalid code. Please try again.";
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        errorMsg =
          "The code you entered is incorrect or expired. Please try again.";
      } else if (err?.message) {
        errorMsg = err.message;
      }
      toast.error(errorMsg);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex w-full max-w-4xl bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="w-1/2 hidden md:block">
          <img
            src="https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=800&q=80"
            alt="MFA Illustration"
            className="object-cover w-full h-full"
          />
        </div>
        <div className="w-full md:w-1/2 flex flex-col justify-center p-10">
          <div className="mb-8">
            <div className="text-2xl font-bold mb-2">
              Multi-Factor Authentication
            </div>
            <div className="text-gray-600 mb-6">
              Please enter the 6-digit verification code from your authenticator
              app to proceed.
            </div>
            <form onSubmit={handleSubmit}>
              <div className="flex gap-3 justify-center mb-6">
                {code.map((digit, idx) => (
                  <input
                    key={idx}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    className="w-14 h-14 text-center text-2xl border-2 border-blue-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm bg-blue-50 text-blue-700 font-bold tracking-widest"
                    value={digit}
                    onChange={(e) => handleChange(idx, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, idx)}
                    id={`mfa-input-${idx}`}
                    autoFocus={idx === 0}
                  />
                ))}
              </div>
              <Button type="submit" className="w-full" disabled={isVerifying}>
                {isVerifying ? "Verifying..." : "Verify Code"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyMfa;
